import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown, ChevronRight, User, TrendingUp, Shield, Home, Hammer, Briefcase, Calculator, Sparkles, BookOpen, Info, Users, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { useAdminRole } from "@/hooks/useAdminRole";
// Use static path for optimized image delivery
const logo = "/images/atralend-logo.jpg";

const Navigation = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [servicesExpanded, setServicesExpanded] = useState(false);
  const location = useLocation();
  const { user } = useAuth();
  const { isAdmin } = useAdminRole();

  const isActive = (path: string) => location.pathname === path;

  const serviceLinks = [
    { name: "Residential", path: "/services/residential", icon: Home },
    { name: "Investment Properties", path: "/services/investment-properties", icon: Briefcase },
    { name: "Commercial", path: "/services/commercial", icon: Hammer },
    { name: "A-Lending", path: "/services/a-lending", icon: Briefcase },
    { name: "B-Lending", path: "/services/b-lending", icon: Briefcase },
    { name: "Private Lending", path: "/services/private-lending", icon: Sparkles },
    { name: "DSCR Loans", path: "/services/dscr-loans", icon: TrendingUp },
    { name: "Bridge Loans", path: "/services/bridge-loans", icon: Zap },
    { name: "Construction Financing", path: "/services/construction-financing", icon: Hammer },
  ];

  const toolsLinks = [
    { name: "Calculators", path: "/calculators", icon: Calculator },
    { name: "AI Assistant", path: "/scenario-assistant", icon: Sparkles },
    { name: "Blog", path: "/blog", icon: BookOpen },
  ];

  const aboutLinks = [
    { name: "Our Story", path: "/about", icon: Info },
    { name: "Capital Partners", path: "/capital-partners", icon: Users },
  ];

  // Mobile nav links (flat list for accordion)
  const mobileNavLinks = [
    { name: "Calculators", path: "/calculators" },
    { name: "AI Assistant", path: "/scenario-assistant" },
    { name: "Blog", path: "/blog" },
    { name: "About", path: "/about" },
    { name: "Capital Partners", path: "/capital-partners" },
    { name: "Contact", path: "/contact" },
  ];

  const isServicesActive = serviceLinks.some((link) => location.pathname === link.path);
  const isToolsActive = toolsLinks.some((link) => location.pathname === link.path);
  const isAboutActive = aboutLinks.some((link) => location.pathname === link.path);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setServicesExpanded(false);
  };

  return (
    <nav className="sticky top-0 z-50 bg-card border-b border-border shadow-sm">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img 
              src={logo} 
              alt="Atralend - Trusted Mortgage Broker in Canada" 
              className="h-14 sm:h-16 w-auto object-contain"
              width={47}
              height={64}
              loading="eager"
            />
            <span className="hidden md:block text-xs text-muted-foreground font-medium border-l border-border pl-3">
              Empowering Your Next Step
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Home Link */}
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive("/")
                  ? "text-primary bg-primary/10"
                  : "text-foreground hover:text-primary hover:bg-primary/5"
              }`}
            >
              Home
            </Link>

            {/* Services Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
               <button
                   className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                     isServicesActive
                       ? "text-primary bg-primary/10"
                       : "text-foreground hover:text-primary hover:bg-primary/5"
                   }`}
                 >
                   Services
                   <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                 </button>
              </DropdownMenuTrigger>
               <DropdownMenuContent align="start" className="w-52 bg-card border border-border shadow-lg z-50">
                 {serviceLinks.map((link) => {
                   const IconComponent = link.icon;
                   return (
                     <DropdownMenuItem key={link.path} asChild>
                       <Link
                         to={link.path}
                         className={`w-full cursor-pointer transition-all duration-200 flex items-center gap-3 px-3 py-2 rounded hover:translate-x-1 ${
                           isActive(link.path) ? "text-primary bg-primary/10" : "hover:bg-primary/5"
                         }`}
                       >
                         <IconComponent className="w-4 h-4 flex-shrink-0 transition-transform group-hover:scale-110" />
                         <span>{link.name}</span>
                       </Link>
                     </DropdownMenuItem>
                   );
                 })}
               </DropdownMenuContent>
            </DropdownMenu>

            {/* Tools Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                 <button
                   className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                     isToolsActive
                       ? "text-primary bg-primary/10"
                       : "text-foreground hover:text-primary hover:bg-primary/5"
                   }`}
                 >
                   Tools
                   <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                 </button>
              </DropdownMenuTrigger>
               <DropdownMenuContent align="start" className="w-48 bg-card border border-border shadow-lg z-50">
                 {toolsLinks.map((link) => {
                   const IconComponent = link.icon;
                   return (
                     <DropdownMenuItem key={link.path} asChild>
                       <Link
                         to={link.path}
                         className={`w-full cursor-pointer transition-all duration-200 flex items-center gap-3 px-3 py-2 rounded hover:translate-x-1 ${
                           isActive(link.path) ? "text-primary bg-primary/10" : "hover:bg-primary/5"
                         }`}
                       >
                         <IconComponent className="w-4 h-4 flex-shrink-0" />
                         <span>{link.name}</span>
                       </Link>
                     </DropdownMenuItem>
                   );
                 })}
               </DropdownMenuContent>
            </DropdownMenu>

            {/* About Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                 <button
                   className={`px-3 py-2 rounded-md text-sm font-medium transition-colors inline-flex items-center gap-1 ${
                     isAboutActive
                       ? "text-primary bg-primary/10"
                       : "text-foreground hover:text-primary hover:bg-primary/5"
                   }`}
                 >
                   About
                   <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-data-[state=open]:rotate-180" />
                 </button>
              </DropdownMenuTrigger>
               <DropdownMenuContent align="start" className="w-44 bg-card border border-border shadow-lg z-50">
                 {aboutLinks.map((link) => {
                   const IconComponent = link.icon;
                   return (
                     <DropdownMenuItem key={link.path} asChild>
                       <Link
                         to={link.path}
                         className={`w-full cursor-pointer transition-all duration-200 flex items-center gap-3 px-3 py-2 rounded hover:translate-x-1 ${
                           isActive(link.path) ? "text-primary bg-primary/10" : "hover:bg-primary/5"
                         }`}
                       >
                         <IconComponent className="w-4 h-4 flex-shrink-0" />
                         <span>{link.name}</span>
                       </Link>
                     </DropdownMenuItem>
                   );
                 })}
               </DropdownMenuContent>
            </DropdownMenu>

            {/* Contact Link */}
            <Link
              to="/contact"
              className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive("/contact")
                  ? "text-primary bg-primary/10"
                  : "text-foreground hover:text-primary hover:bg-primary/5"
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Contact & CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href="tel:613-850-2237"
              className="flex items-center gap-2 text-sm font-medium text-foreground hover:text-primary transition-colors"
            >
              <Phone className="w-4 h-4" />
              <span>613-850-2237</span>
            </a>
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm">
                    <User className="w-4 h-4 mr-2" />
                    My Dashboard
                    <ChevronDown className="w-4 h-4 ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48 bg-card border border-border shadow-lg z-50">
                  <DropdownMenuItem asChild>
                    <Link to="/borrower/dashboard" className="w-full cursor-pointer">
                      Borrower Portal
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/investor/dashboard" className="w-full cursor-pointer">
                      Investor Portal
                    </Link>
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/admin/leads" className="w-full cursor-pointer flex items-center gap-2">
                          <Shield className="w-4 h-4" />
                          Lead Dashboard
                        </Link>
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/auth">
                <Button variant="outline" size="sm">
                  Login
                </Button>
              </Link>
            )}
            <Link to="/pre-approval">
              <Button variant="hero" size="sm">
                Get Pre-Approved
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button - Touch optimized 44px minimum */}
          <button
            className="lg:hidden min-h-[44px] min-w-[44px] flex items-center justify-center rounded-md hover:bg-muted transition-colors active:bg-muted/80"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Navigation - Full screen overlay */}
        <div
          className={`lg:hidden fixed inset-x-0 top-16 bottom-0 bg-card z-40 transition-all duration-300 ease-in-out ${
            mobileMenuOpen ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4 pointer-events-none"
          }`}
        >
          <div className="h-full overflow-y-auto pb-safe">
            <div className="flex flex-col py-2">
              {/* Home Link */}
              <Link
                to="/"
                onClick={closeMobileMenu}
                className={`flex items-center min-h-[48px] px-6 text-base font-medium transition-colors ${
                  isActive("/")
                    ? "text-primary bg-primary/10"
                    : "text-foreground hover:text-primary hover:bg-primary/5 active:bg-primary/10"
                }`}
              >
                Home
              </Link>

              {/* Services Accordion */}
              <div>
                <button
                  onClick={() => setServicesExpanded(!servicesExpanded)}
                  className={`flex items-center justify-between w-full min-h-[48px] px-6 text-base font-medium transition-colors ${
                    isServicesActive
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:text-primary hover:bg-primary/5 active:bg-primary/10"
                  }`}
                >
                  <span>Services</span>
                  <ChevronRight
                    className={`w-5 h-5 transition-transform duration-200 ${
                      servicesExpanded ? "rotate-90" : ""
                    }`}
                  />
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ${
                    servicesExpanded ? "max-h-[400px]" : "max-h-0"
                  }`}
                >
                  <div className="bg-muted/50 py-1">
                    {serviceLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={closeMobileMenu}
                        className={`flex items-center min-h-[44px] pl-10 pr-6 text-sm transition-colors ${
                          isActive(link.path)
                            ? "text-primary font-medium"
                            : "text-muted-foreground hover:text-primary active:text-primary"
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>

              {/* Other Nav Links */}
              {mobileNavLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={closeMobileMenu}
                  className={`flex items-center min-h-[48px] px-6 text-base font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-primary bg-primary/10"
                      : "text-foreground hover:text-primary hover:bg-primary/5 active:bg-primary/10"
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Mobile CTA Section */}
              <div className="mt-4 mx-4 pt-4 border-t border-border">
                {/* Dashboard or Login Link */}
                {user ? (
                  <>
                    <Link to="/borrower/dashboard" onClick={closeMobileMenu} className="block mb-2">
                      <Button variant="secondary" className="w-full min-h-[48px] gap-2 text-base">
                        <User className="w-5 h-5" />
                        Borrower Dashboard
                      </Button>
                    </Link>
                    <Link to="/investor/dashboard" onClick={closeMobileMenu} className="block mb-2">
                      <Button variant="outline" className="w-full min-h-[48px] gap-2 text-base">
                        <TrendingUp className="w-5 h-5" />
                        Investor Dashboard
                      </Button>
                    </Link>
                    {isAdmin && (
                      <Link to="/admin/leads" onClick={closeMobileMenu} className="block mb-3">
                        <Button variant="ghost" className="w-full min-h-[48px] gap-2 text-base">
                          <Shield className="w-5 h-5" />
                          Lead Dashboard
                        </Button>
                      </Link>
                    )}
                  </>
                ) : (
                  <Link to="/auth" onClick={closeMobileMenu} className="block mb-3">
                    <Button variant="secondary" className="w-full min-h-[48px] gap-2 text-base">
                      <User className="w-5 h-5" />
                      Login / Sign Up
                    </Button>
                  </Link>
                )}
                {/* Prominent Click-to-Call */}
                <a href="tel:613-850-2237" className="block mb-3">
                  <Button variant="outline" className="w-full min-h-[48px] gap-2 text-base">
                    <Phone className="w-5 h-5" />
                    Call 613-850-2237
                  </Button>
                </a>
                <Link to="/pre-approval" onClick={closeMobileMenu}>
                  <Button variant="hero" className="w-full min-h-[48px] text-base">
                    Get Pre-Approved
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Backdrop */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black/20 z-30"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}
    </nav>
  );
};

export default Navigation;
